import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { Badge } from '@components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@components/ui/dialog'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { organizationService } from '@services/organizations.service'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building, Building2, MapPin, Plus, Trash2 } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'

const organizationStatusLabels: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  suspended: 'Suspendido',
}

export function OrganizationsPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'organizations' | 'business-units' | 'locations'>('organizations')
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false)
  const [isBUDialogOpen, setIsBUDialogOpen] = useState(false)
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false)

  // Form states
  const [orgForm, setOrgForm] = useState({ name: '', description: '', status: 'active' })
  const [buForm, setBuForm] = useState({ name: '', description: '', organization_id: '', status: 'active' })
  const [locationForm, setLocationForm] = useState({ 
    name: '', address: '', city: '', state: '', zip_code: '', country: '', phone: '', email: '', business_unit_id: '' 
  })

  const { data: orgsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationService.getOrganizations(),
  })

  const { data: busData } = useQuery({
    queryKey: ['business-units'],
    queryFn: () => organizationService.getBusinessUnits(),
  })

  const { data: locsData } = useQuery({
    queryKey: ['locations'],
    queryFn: () => organizationService.getLocations(),
  })

  const createOrgMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; status?: string }) => 
      organizationService.createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      setIsOrgDialogOpen(false)
      setOrgForm({ name: '', description: '', status: 'active' })
    },
  })

  const createBUMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; organization_id: string; status?: string }) => 
      organizationService.createBusinessUnit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-units'] })
      setIsBUDialogOpen(false)
      setBuForm({ name: '', description: '', organization_id: '', status: 'active' })
    },
  })

  const createLocationMutation = useMutation({
    mutationFn: (data: typeof locationForm) => 
      organizationService.createLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] })
      setIsLocationDialogOpen(false)
      setLocationForm({ name: '', address: '', city: '', state: '', zip_code: '', country: '', phone: '', email: '', business_unit_id: '' })
    },
  })

  const deleteOrgMutation = useMutation({
    mutationFn: (id: string) => organizationService.deleteOrganization(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organizations'] }),
  })

  const deleteBUMutation = useMutation({
    mutationFn: (id: string) => organizationService.deleteBusinessUnit(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['business-units'] }),
  })

  const deleteLocationMutation = useMutation({
    mutationFn: (id: string) => organizationService.deleteLocation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations'] }),
  })

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Organizaciones</h1>
          <p className="text-muted-foreground">Gestionar organizaciones, unidades de negocio y ubicaciones</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="organizations">Organizaciones</TabsTrigger>
          <TabsTrigger value="business-units">Unidades de Negocio</TabsTrigger>
          <TabsTrigger value="locations">Ubicaciones</TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isOrgDialogOpen} onOpenChange={setIsOrgDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Organización
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Organización</DialogTitle>
                  <DialogDescription>Agregar una nueva organización al sistema.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="org-name">Nombre</Label>
                    <Input
                      id="org-name"
                      value={orgForm.name}
                      onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                      placeholder="Nombre de la organización"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="org-desc">Descripción</Label>
                    <Textarea
                      id="org-desc"
                      value={orgForm.description}
                      onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })}
                      placeholder="Descripción de la organización"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="org-status">Estado</Label>
                    <Select value={orgForm.status} onValueChange={(v) => setOrgForm({ ...orgForm, status: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Activo</SelectItem>
                        <SelectItem value="inactive">Inactivo</SelectItem>
                        <SelectItem value="suspended">Suspendido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsOrgDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={() => createOrgMutation.mutate(orgForm)}>Crear</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Organizaciones</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Creado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgsData?.organizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          {org.name}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md truncate">{org.description}</TableCell>
                      <TableCell>
                        <Badge variant={org.status === 'active' ? 'default' : 'secondary'}>
                          {organizationStatusLabels[org.status] ?? org.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(org.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => deleteOrgMutation.mutate(org.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Units Tab */}
        <TabsContent value="business-units" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isBUDialogOpen} onOpenChange={setIsBUDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Unidad de Negocio
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Unidad de Negocio</DialogTitle>
                  <DialogDescription>Agregar una nueva unidad de negocio.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="bu-name">Nombre</Label>
                    <Input
                      id="bu-name"
                      value={buForm.name}
                      onChange={(e) => setBuForm({ ...buForm, name: e.target.value })}
                      placeholder="Nombre de la unidad de negocio"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="bu-desc">Descripción</Label>
                    <Textarea
                      id="bu-desc"
                      value={buForm.description}
                      onChange={(e) => setBuForm({ ...buForm, description: e.target.value })}
                      placeholder="Descripción de la unidad de negocio"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="bu-org">Organización</Label>
                    <Select value={buForm.organization_id} onValueChange={(v) => setBuForm({ ...buForm, organization_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar organización" />
                      </SelectTrigger>
                      <SelectContent>
                        {orgsData?.organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsBUDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={() => createBUMutation.mutate(buForm)}>Crear</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Unidades de Negocio</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Organización</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Creado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {busData?.business_units.map((bu) => (
                    <TableRow key={bu.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {bu.name}
                        </div>
                      </TableCell>
                      <TableCell>{bu.organization?.name}</TableCell>
                      <TableCell>
                        <Badge variant={bu.status === 'active' ? 'default' : 'secondary'}>
                          {bu.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(bu.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => deleteBUMutation.mutate(bu.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isLocationDialogOpen} onOpenChange={setIsLocationDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Ubicación
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Ubicación</DialogTitle>
                  <DialogDescription>Agregar una nueva ubicación.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="loc-name">Nombre</Label>
                    <Input
                      id="loc-name"
                      value={locationForm.name}
                      onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                      placeholder="Nombre de la ubicación"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="loc-bu">Unidad de Negocio</Label>
                    <Select value={locationForm.business_unit_id} onValueChange={(v) => setLocationForm({ ...locationForm, business_unit_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar unidad de negocio" />
                      </SelectTrigger>
                      <SelectContent>
                        {busData?.business_units.map((bu) => (
                          <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="loc-address">Dirección</Label>
                    <Input
                      id="loc-address"
                      value={locationForm.address}
                      onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                      placeholder="Dirección"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Ciudad"
                      value={locationForm.city}
                      onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })}
                    />
                    <Input
                      placeholder="Estado/Provincia"
                      value={locationForm.state}
                      onChange={(e) => setLocationForm({ ...locationForm, state: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Código Postal"
                      value={locationForm.zip_code}
                      onChange={(e) => setLocationForm({ ...locationForm, zip_code: e.target.value })}
                    />
                    <Input
                      placeholder="País"
                      value={locationForm.country}
                      onChange={(e) => setLocationForm({ ...locationForm, country: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsLocationDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={() => createLocationMutation.mutate(locationForm)}>Crear</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Ubicaciones</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Unidad de Negocio</TableHead>
                    <TableHead>Dirección</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locsData?.locations.map((loc) => (
                    <TableRow key={loc.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {loc.name}
                        </div>
                      </TableCell>
                      <TableCell>{loc.business_unit?.name}</TableCell>
                      <TableCell>{[loc.address, loc.city, loc.state].filter(Boolean).join(', ')}</TableCell>
                      <TableCell>{loc.phone || loc.email}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => deleteLocationMutation.mutate(loc.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
